import { useEffect, useCallback, useState, useRef, useMemo } from 'react'
import { usePermissions } from '../lib/usePermissions.jsx'
import { useReactQueryDataContext } from '../lib/ReactQueryDataProvider'
import { preloadOnInteraction } from '../lib/componentPreloader.js'
import { isFeatureEnabled } from '../lib/featureFlags'
import { usePlanFeatures } from '../hooks/usePlanFeatures'
import { usePlan } from '../lib/usePlan'

// Display order and labels for the grouped sidebar sections. Tabs whose
// `group` field matches one of these keys render under the matching header.
const GROUP_ORDER = ['work', 'money', 'people', 'ai', 'business']
const GROUP_LABELS = {
  work: 'Operations',
  money: 'Financials',
  people: 'Team & Clients',
  ai: 'Intelligence',
  business: 'Workspace',
}
const SIDEBAR_GROUPS_KEY = 'prosperia.sidebar.expandedGroups'

const Sidebar = ({ activeTab, setActiveTab, isOpen = true, onClose, isCollapsed = false, onToggleCollapsed }) => {
  const { 
    permissions, 
    loading, 
    isOwner, 
    getAccessibleTabNames,
    canReadExpenses
  } = usePermissions()

  // Get plan-based feature access
  const { hasFeature, loading: featuresLoading } = usePlanFeatures()
  
  // Get current plan tier
  const { plan: planTier } = usePlan()
  
  // Check if account is on free plan
  const isFreePlan = planTier === 'free' || !planTier || planTier === null

  // Use ReactQueryDataProvider for company data
  const {
    companies,
    company_branding,
    employees,
    loading: dataLoading,
    error: dataError,
    isDataReady,
    isInitializing
  } = useReactQueryDataContext()

  const companyData = companies?.data?.[0] || null
  const companyBranding = company_branding?.data?.[0] || null
  const brandingLoading = loading || dataLoading || isInitializing || false

  // Count active employees for conditional feature visibility
  const activeEmployeeCount = useMemo(() => {
    return employees?.data?.filter(e => e.status === 'active')?.length || 0
  }, [employees?.data])

  // Debug logging for branding data - only in development and when there are changes
  if (process.env.NODE_ENV === 'development') {
    console.log('[Sidebar] Branding data state:', {
      hasBrandingData: !!companyBranding,
      hasLogo: !!companyBranding?.logo_url,
      logoUrl: companyBranding?.logo_url,
      brandingLoading,
      dataLoading: dataLoading.isInitializing,
      isDataReady,
      rawBrandingData: company_branding.data,
      companyBrandingObject: companyBranding
    })
  }

  // Check if FontAwesome is loaded
  const [fontAwesomeLoaded, setFontAwesomeLoaded] = useState(false)
  
  useEffect(() => {
    const checkFontAwesome = () => {
      if (window.fontAwesomeLoaded) {
        setFontAwesomeLoaded(true)
        return
      }
      
      // Check if FontAwesome CSS is loaded by testing a class
      const testElement = document.createElement('i')
      testElement.className = 'fas fa-test'
      testElement.style.position = 'absolute'
      testElement.style.left = '-9999px'
      document.body.appendChild(testElement)
      
      const computedStyle = window.getComputedStyle(testElement, '::before')
      const hasFontAwesome = computedStyle.fontFamily && computedStyle.fontFamily.includes('Font Awesome')
      
      document.body.removeChild(testElement)
      
      if (hasFontAwesome) {
        setFontAwesomeLoaded(true)
        window.fontAwesomeLoaded = true
      } else {
        // Retry after a short delay
        setTimeout(checkFontAwesome, 100)
      }
    }
    
    checkFontAwesome()
  }, [])

  // Define all possible tabs with their import functions for prefetching.
  // `group` controls placement in the sidebar. `null` means pinned outside groups
  // (Dashboard pinned top, Settings pinned bottom).
  const allTabs = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt', group: null, importFn: () => import('../features/dashboard/components/DailyDashboard') },
    { id: 'scheduling', label: 'Scheduling', icon: 'fas fa-calendar-alt', group: 'work', importFn: () => import('../features/scheduling/Scheduling') },
    { id: 'jobs', label: 'Jobs', icon: 'fas fa-briefcase', group: 'work', importFn: () => import('../features/jobs/JobsList') },
    { id: 'estimates', label: 'Estimates', icon: 'fas fa-calculator', group: 'work', importFn: () => import('../features/estimates/Estimates') },
    { id: 'invoices', label: 'Invoices', icon: 'fas fa-file-invoice-dollar', group: 'work', importFn: () => import('../features/invoices/Invoices') },
    ...(isFeatureEnabled('INSPECTION_ENABLED') && (hasFeature('inspection_access') || isFreePlan) ? [{ id: 'inspection', label: 'Inspection', icon: 'fas fa-clipboard-check', group: 'work', importFn: () => import('../features/inspection/Inspection') }] : []),
    { id: 'expenses', label: 'Expenses', icon: 'fas fa-receipt', group: 'money', importFn: () => import('../features/expenses/Expenses') },
    { id: 'finances', label: 'Finances', icon: 'fas fa-wallet', group: 'money', importFn: () => import('../features/finances/Finances') },
    { id: 'clients', label: 'Clients', icon: 'fas fa-users', group: 'people', importFn: () => import('../features/clients/Clients') },
    { id: 'employees', label: 'Employees', icon: 'fas fa-user-tie', group: 'people', importFn: () => import('../features/employees/Employees') },
    ...(activeEmployeeCount >= 2 ? [{ id: 'teams', label: 'Teams', icon: 'fas fa-users-cog', group: 'people', importFn: () => import('../features/employees/Teams') }] : []),
    ...(activeEmployeeCount >= 1 ? [{ id: 'time-off', label: 'Time Off', icon: 'fas fa-calendar-minus', group: 'people', importFn: () => import('../features/timeoff/TimeOff') }] : []),
    { id: 'ai-assistant', label: 'AI Assistant', icon: 'fas fa-robot', group: 'ai', importFn: () => import('../features/toolbelt/ai-assistant/ChatPanel') },
    ...(isFeatureEnabled('TOOLBELT_ENABLED') ? [{ id: 'toolbelt', label: 'Tools', icon: 'fas fa-toolbox', group: 'ai', importFn: () => import('../features/toolbelt/ToolBelt') }] : []),
    { id: 'services', label: 'Services', icon: 'fas fa-cogs', group: 'business', importFn: () => import('../features/services/Services') },
    { id: 'automations', label: 'Automations', icon: 'fas fa-magic', group: 'business', importFn: () => import('../features/automations/AutomationsMain') },
    ...(hasFeature('widgets_access') || isFreePlan ? [{ id: 'widgets', label: 'Website Form', icon: 'fas fa-code', group: 'business', importFn: () => import('../features/widgets/EmbedWidgetManager') }] : []),
    { id: 'files', label: 'Files', icon: 'fas fa-folder', group: 'business', importFn: () => import('../features/files/Files') },
    { id: 'settings', label: 'Settings', icon: 'fas fa-cog', group: null, importFn: () => import('../features/settings/Settings') },
    // POST MVP: Communications tab - hidden for MVP release
    // { id: 'communications', label: 'Communications', icon: 'fas fa-comments', group: 'business', importFn: () => import('../features/communications/Communications') },
  ], [hasFeature, isFreePlan, activeEmployeeCount])

  // Filter tabs based on user permissions
  const getVisibleTabs = useCallback(() => {
    // Free plan users see all tabs
    if (isFreePlan) {
      return allTabs
    }
    
    if (isOwner) {
      // Owners see all tabs
      return allTabs
    }
    
    // Employees only see tabs they have permission for
    const accessibleTabNames = getAccessibleTabNames()
    return allTabs.filter(tab => {
      // Always show settings to employees (cannot be restricted)
      if (tab.id === 'settings') {
        return true
      }
      if (tab.id === 'expenses' || tab.id === 'finances') {
        return canReadExpenses()
      }
      return accessibleTabNames.includes(tab.id)
    })
  }, [isOwner, isFreePlan, getAccessibleTabNames, canReadExpenses, allTabs])

  const tabs = getVisibleTabs()

  // Bucket visible tabs into pinned-top, groups, and pinned-bottom for rendering.
  const { pinnedTopTab, pinnedBottomTab, tabsByGroup } = useMemo(() => {
    const top = tabs.find(t => t.id === 'dashboard') || null
    const bottom = tabs.find(t => t.id === 'settings') || null
    const byGroup = {}
    for (const key of GROUP_ORDER) byGroup[key] = []
    for (const tab of tabs) {
      if (tab.group && byGroup[tab.group]) byGroup[tab.group].push(tab)
    }
    return { pinnedTopTab: top, pinnedBottomTab: bottom, tabsByGroup: byGroup }
  }, [tabs])

  // Track which groups are expanded. Persisted to localStorage. Defaults to all open.
  // The group containing the active tab is always force-expanded for discoverability.
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SIDEBAR_GROUPS_KEY) : null
      if (raw) {
        const parsed = JSON.parse(raw)
        const merged = {}
        for (const key of GROUP_ORDER) merged[key] = parsed[key] !== false
        return merged
      }
    } catch (_) { /* intentionally empty */ }
    const defaults = {}
    for (const key of GROUP_ORDER) defaults[key] = true
    return defaults
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(expandedGroups))
    } catch (_) { /* intentionally empty */ }
  }, [expandedGroups])

  const toggleGroup = useCallback((key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  // The group containing the active tab — used to force-expand it so the user
  // never has to hunt for the page they're currently on.
  const activeGroup = useMemo(() => {
    const found = tabs.find(t => t.id === activeTab)
    return found?.group || null
  }, [tabs, activeTab])

  // Handle tab navigation with error handling and fallback
  const handleTabClick = useCallback(async (tabId) => {
    try {
      // Prevent multiple rapid clicks
      if (activeTab === tabId) return
      
      // Set loading state if needed
      setActiveTab(tabId)
      
      // Add a small delay to ensure state update
      await new Promise(resolve => setTimeout(resolve, 50))
      // Close sidebar on mobile after navigation
      if (typeof onClose === 'function') {
        onClose()
      }
      
    } catch (error) {
      console.error('Navigation error in sidebar:', error)
      
      // Fallback navigation using window.location
      try {
        const routeMap = {
          'dashboard': '/dashboard',
          // Conditionally include toolbelt route
          ...(isFeatureEnabled('TOOLBELT_ENABLED') ? { 'toolbelt': '/toolbelt' } : {}),
          'ai-assistant': '/ai',
          'settings': '/settings',
          'clients': '/clients',
          'jobs': '/jobs',
          'estimates': '/estimates',
          'services': '/services',
          'invoices': '/invoices',
          'expenses': '/expenses',
          'finances': '/finances',
          'employees': '/employees',
          'teams': '/teams',
          'time-off': '/time-off',
          'scheduling': '/scheduling',
          // POST MVP: Communications route - hidden for MVP release
          // 'communications': '/communications',
          // Reports consolidated into Finances
          // Integrations live under Settings → QuickBooks (no dedicated sidebar entry)
          'automations': '/automations',
          // Conditionally include inspection route (POST-MVP FEATURE)
          ...(isFeatureEnabled('INSPECTION_ENABLED') ? { 'inspection': '/inspection' } : {}),
          'widgets': '/widgets'
          // 'files': '/files' // Uncomment when Files component is created
        }
        
        const path = routeMap[tabId]
        if (path) {
          window.location.href = path
        }
      } catch (fallbackError) {
        console.error('Fallback navigation also failed:', fallbackError)
        // Last resort - reload the page (navigation failure, not permission-related)
        window.location.reload()
      }
    }
  }, [activeTab, setActiveTab])

    // Company branding and data now loaded via ReactQueryDataProvider

  useEffect(() => {
    // Add any necessary side effects here
  }, [])

  // Sidebar DOM ref and outside-click/Escape handling (must be before any early returns)
  const sidebarRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    const handleOutside = (event) => {
      try {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
          event.preventDefault()
          if (typeof onClose === 'function') onClose()
        }
      } catch (_) { /* intentionally empty */ }
    }
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        if (typeof onClose === 'function') onClose()
      }
    }
    document.addEventListener('pointerdown', handleOutside, true)
    document.addEventListener('touchstart', handleOutside, true)
    document.addEventListener('keydown', handleEsc, true)
    return () => {
      document.removeEventListener('pointerdown', handleOutside, true)
      document.removeEventListener('touchstart', handleOutside, true)
      document.removeEventListener('keydown', handleEsc, true)
    }
  }, [isOpen, onClose])

  // Show loading state while permissions are being loaded OR data is initializing
  if (loading || (isInitializing && !isDataReady)) {
    // Use branding data if available, even during loading
    const displayName = companyData?.name || 'Prosperia'
    const logoUrl = companyBranding?.logo_url
    
    return (
      <div className={`fixed left-0 top-0 h-[100dvh] sidebar-container shadow-lg z-50 flex flex-col transform-gpu [backface-visibility:hidden] [will-change:transform,width] transition-[transform,width] duration-300 ${isCollapsed ? 'w-72 md:w-16' : 'w-72'} ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="flex-shrink-0 p-6 ios:pt-[env(safe-area-inset-top)] border-b border-subtle">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={`${displayName} Logo`}
                  className="w-full h-full object-contain rounded-lg"
                  onError={(e) => {
                    console.warn('[Sidebar] Company logo failed to load, falling back to Prosperia logo')
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'block'
                  }}
                />
              ) : null}
              <picture style={{ display: logoUrl ? 'none' : 'block' }}>
                <source srcSet="/icons/logo_1024x1024-noBGm.webp" type="image/webp" />
                <img 
                  src="/icons/logo_1024x1024-noBGm.png" 
                  alt="Prosperia Logo"
                  className="w-full h-full object-contain rounded-lg"
                  onError={(e) => {
                    console.warn('[Sidebar] Prosperia logo failed to load, falling back to text')
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'block'
                  }}
                />
              </picture>
            </div>
            <div>
              <h1 className="text-xl font-bold sidebar-text">{displayName}</h1>
              {/* Removed CRM System text as requested */}
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-default border-t-accent-primary mx-auto mb-2"></div>
            <p className="text-sm sidebar-subtitle">Loading permissions...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={sidebarRef}
      className={`fixed left-0 top-0 h-[100dvh] sidebar-container shadow-lg z-50 flex flex-col transform-gpu [backface-visibility:hidden] [will-change:transform,width] transition-[transform,width] duration-300 ${
        isCollapsed ? 'w-72 md:w-16' : 'w-72'
      } ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
    >
      {/* Fixed header section - Owner info with lighter background */}
      <div className={`flex-shrink-0 ios:pt-[env(safe-area-inset-top)] border-b border-blue-600/20 sidebar-owner-section ${isCollapsed ? 'p-6 md:px-3 md:py-4' : 'p-6'}`}>
        <div className={`flex items-center ${isCollapsed ? 'space-x-3 md:space-x-0 md:justify-center' : 'space-x-3'}`}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
            {companyBranding?.logo_url ? (
              <img
                src={companyBranding.logo_url}
                alt={`${companyData?.name || 'Company'} Logo`}
                className="w-full h-full object-contain rounded-lg"
                onError={(e) => {
                  console.warn('[Sidebar] Company logo failed to load, falling back to Prosperia logo')
                  e.target.style.display = 'none'
                  e.target.nextSibling.style.display = 'block'
                }}
              />
            ) : null}
            <picture style={{ display: companyBranding?.logo_url ? 'none' : 'block' }}>
              <source srcSet="/icons/logo_1024x1024-noBGm.webp" type="image/webp" />
              <img
                src="/icons/logo_1024x1024-noBGm.png"
                alt="Prosperia Logo"
                className="w-full h-full object-contain rounded-lg"
                onError={(e) => {
                  console.warn('[Sidebar] Prosperia logo failed to load, falling back to text')
                  e.target.style.display = 'none'
                  e.target.nextSibling.style.display = 'block'
                }}
              />
            </picture>
          </div>
          <div className={`min-w-0 flex-1 ${isCollapsed ? 'md:hidden' : ''}`}>
            <h1 className="text-xl font-bold sidebar-text truncate">
              {companyData?.name || companyBranding?.company_name || 'Prosperia'}
            </h1>
            <div className="flex items-center space-x-1 mt-1">
              <div className={`w-2 h-2 rounded-full ${isOwner ? 'bg-accent-success' : 'bg-accent-primary'}`}></div>
              <span className="text-xs sidebar-subtitle">
                {isOwner ? 'Owner' : 'Employee'}
              </span>
            </div>
          </div>
          {/* Close button (mobile only) */}
          <button
            type="button"
            aria-label="Close sidebar"
            onClick={onClose}
            onPointerDown={(e) => { e.preventDefault(); if (typeof onClose === 'function') onClose() }}
            onTouchStart={(e) => { e.preventDefault(); if (typeof onClose === 'function') onClose() }}
            className="md:hidden ml-auto inline-flex items-center justify-center w-9 h-9 rounded-md text-secondary hover:text-primary hover:bg-hover focus:outline-none focus:ring-2 focus:ring-accent-primary pointer-events-auto"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                if (typeof onClose === 'function') onClose()
              }
            }}
          >
            {fontAwesomeLoaded ? (
              <i className="fas fa-times"></i>
            ) : (
              <div className="w-4 h-4 bg-muted rounded"></div>
            )}
          </button>
        </div>
      </div>

      {/* Subtle separator line */}
      <div className={`h-px bg-gradient-to-r from-transparent via-border-default/30 to-transparent ${isCollapsed ? 'mx-4 md:mx-2' : 'mx-4'}`}></div>

      {/* Scrollable navigation section */}
      <div className="flex-1 overflow-y-auto ios:pb-[env(safe-area-inset-bottom)] overscroll-contain ios:[-webkit-overflow-scrolling:touch] touch-pan-y transform-gpu [transform:translateZ(0)] [backface-visibility:hidden] scrollbar-hide">
        <div className={`pt-4 ${isCollapsed ? 'px-6 pb-6 md:px-2 md:pb-3' : 'p-6'}`}>
          <nav className="space-y-1">
            {/* Pinned top: Dashboard */}
            {pinnedTopTab && (
              <NavButton
                tab={pinnedTopTab}
                isActive={activeTab === pinnedTopTab.id}
                isCollapsed={isCollapsed}
                fontAwesomeLoaded={fontAwesomeLoaded}
                onClick={() => handleTabClick(pinnedTopTab.id)}
              />
            )}

            {/* Grouped sections */}
            {GROUP_ORDER.map((groupKey, groupIdx) => {
              const items = tabsByGroup[groupKey]
              if (!items || items.length === 0) return null
              // Always treat the group containing the active tab as expanded so the
              // current page is reachable without an extra click.
              const isExpanded = expandedGroups[groupKey] || activeGroup === groupKey
              return (
                <div key={groupKey} className="pt-3 first:pt-2">
                  {/* Group header — hidden in collapsed rail mode (md+ only) */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupKey)}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${GROUP_LABELS[groupKey]} section`}
                    className={`w-full flex items-center justify-between px-3 py-1 mb-1 rounded sidebar-section-header ${isCollapsed ? 'md:hidden' : ''}`}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wider sidebar-subtitle">
                      {GROUP_LABELS[groupKey]}
                    </span>
                    {fontAwesomeLoaded && (
                      <i
                        className={`fas fa-chevron-down text-[10px] sidebar-subtitle transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
                        aria-hidden="true"
                      ></i>
                    )}
                  </button>

                  {/* In rail mode, replace the header with a thin divider between groups */}
                  {groupIdx > 0 && (
                    <div className={`hidden ${isCollapsed ? 'md:block' : ''} h-px bg-mute mx-2 my-2`}></div>
                  )}

                  {/* Items visibility:
                      - Group expanded → always shown
                      - Group collapsed + sidebar expanded → hidden everywhere
                      - Group collapsed + sidebar in rail mode → hidden on mobile drawer (where the
                        section header is visible and lets the user expand), shown at md+ since the
                        rail has no headers and would otherwise look empty. */}
                  <div
                    className={`space-y-1 ${
                      !isExpanded ? (isCollapsed ? 'hidden md:block' : 'hidden') : ''
                    }`}
                  >
                    {items.map(tab => (
                      <NavButton
                        key={tab.id}
                        tab={tab}
                        isActive={activeTab === tab.id}
                        isCollapsed={isCollapsed}
                        fontAwesomeLoaded={fontAwesomeLoaded}
                        onClick={() => handleTabClick(tab.id)}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </nav>

          {/* Pinned bottom: Settings */}
          <div className="mt-6 pt-4 border-t border-subtle">
            {pinnedBottomTab && (
              <NavButton
                tab={pinnedBottomTab}
                isActive={activeTab === pinnedBottomTab.id}
                isCollapsed={isCollapsed}
                fontAwesomeLoaded={fontAwesomeLoaded}
                onClick={() => handleTabClick(pinnedBottomTab.id)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Sticky footer with desktop-only collapse toggle. Sits outside the scroll
          area so it's always reachable and doesn't compete with nav items. */}
      {typeof onToggleCollapsed === 'function' && (
        <div className="hidden md:flex flex-shrink-0 border-t border-subtle px-2 py-2 justify-end">
          <button
            type="button"
            onClick={onToggleCollapsed}
            className={`inline-flex items-center justify-center rounded-md text-secondary hover:text-primary hover:bg-hover transition-colors ${
              isCollapsed ? 'w-full h-8' : 'w-8 h-8'
            }`}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {fontAwesomeLoaded ? (
              <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'left'} text-xs`} aria-hidden="true"></i>
            ) : (
              <div className="w-3 h-3 bg-muted rounded"></div>
            )}
          </button>
        </div>
      )}
    </div>
  )
}

// Reusable nav button. Lives outside Sidebar so it doesn't get re-created on
// every render and isn't subject to identity churn that would break preload caching.
const NavButton = ({ tab, isActive, isCollapsed, fontAwesomeLoaded, onClick }) => {
  const handlePreload = () => {
    if (tab.importFn && !isActive) {
      preloadOnInteraction(tab.id, tab.importFn).catch(error => {
        console.warn(`Failed to preload ${tab.id}:`, error)
      })
    }
  }
  return (
    <button
      onClick={onClick}
      onMouseEnter={handlePreload}
      onFocus={handlePreload}
      disabled={isActive}
      className={`group w-full flex items-center rounded-lg text-left transition-colors duration-200 sidebar-nav-item ${
        isActive ? 'active' : ''
      } ${
        isCollapsed
          ? 'space-x-3 px-4 py-3 md:space-x-0 md:justify-center md:px-2 md:py-2.5'
          : 'space-x-3 px-4 py-3'
      }`}
      aria-label={`Navigate to ${tab.label}`}
      title={tab.label}
    >
      {fontAwesomeLoaded ? (
        <i className={`${tab.icon} w-5 text-center flex-shrink-0`} aria-hidden="true"></i>
      ) : (
        <div className="w-5 h-5 bg-muted rounded animate-pulse flex-shrink-0"></div>
      )}
      <span className={`font-medium truncate ${isCollapsed ? 'md:hidden' : ''}`}>{tab.label}</span>
    </button>
  )
}

export { Sidebar }
